/**
 * Temporal Wrapper with Native Detection
 *
 * This module provides a unified interface to the Temporal API that:
 * 1. Detects if native Temporal is available in the browser
 * 2. Uses native Temporal when available (better performance)
 * 3. Falls back to temporal-polyfill-lite when native is not available
 *
 * Native Temporal Support (as of Feb 2026):
 * - Chrome 144+: Full support
 * - Edge 144+: Full support (Chromium-based)
 * - Firefox 139+: Full support
 * - Safari: NOT supported (requires polyfill)
 */ // Import from dedicated polyfill import file to avoid circular dependency
// (webpack aliases temporal-polyfill-lite → this file, but NOT TemporalPolyfillImport)
import { Temporal as TemporalPolyfill } from "./TemporalPolyfillImport";
/**
 * Detects if native Temporal API is available and functional
 */ export function hasNativeTemporalSupport() {
    try {
        // Check if Temporal exists on globalThis
        if (typeof globalThis.Temporal === "undefined") {
            return false;
        }
        // Verify it's actually functional by testing a simple operation
        const nativeTemporal = globalThis.Temporal;
        // Test that core functionality works
        const testDate = nativeTemporal.PlainDate.from("2026-01-01");
        const testResult = testDate.year === 2026 && testDate.month === 1 && testDate.day === 1;
        if (!testResult) {
            return false;
        }
        // Test ZonedDateTime which is more complex
        const testZoned = nativeTemporal.Now.zonedDateTimeISO();
        if (!testZoned || typeof testZoned.toPlainDate !== "function") {
            return false;
        }
        // Test the explicit-timeZone-argument overloads used throughout production code
        // (e.g. TaskStore.ts, DateUtilities-temporal.ts). The untargeted calls above only
        // exercise the system-default timezone; some native implementations have been
        // observed to reject an explicit IANA timezone string here even though the
        // no-argument form works, so this must be verified separately.
        const testZonedWithTimeZone = nativeTemporal.Now.zonedDateTimeISO("America/New_York");
        if (!testZonedWithTimeZone || typeof testZonedWithTimeZone.toPlainDate !== "function") {
            return false;
        }
        const testPlainDateWithTimeZone = nativeTemporal.Now.plainDateISO("America/New_York");
        if (!testPlainDateWithTimeZone || typeof testPlainDateWithTimeZone.toString !== "function") {
            return false;
        }
        return true;
    } catch  {
        return false;
    }
}
/**
 * Detects browser information from user agent
 */ function detectBrowser() {
    if (typeof navigator === "undefined") {
        return {};
    }
    const ua = navigator.userAgent;
    // Check for common browsers (order matters - Edge includes Chrome)
    if (ua.includes("Edg/")) {
        const match = ua.match(/Edg\/(\d+)/);
        return {
            name: "Edge",
            version: match?.[1]
        };
    }
    if (ua.includes("Chrome/")) {
        const match = ua.match(/Chrome\/(\d+)/);
        return {
            name: "Chrome",
            version: match?.[1]
        };
    }
    if (ua.includes("Firefox/")) {
        const match = ua.match(/Firefox\/(\d+)/);
        return {
            name: "Firefox",
            version: match?.[1]
        };
    }
    if (ua.includes("Safari/") && !ua.includes("Chrome")) {
        const match = ua.match(/Version\/(\d+)/);
        return {
            name: "Safari",
            version: match?.[1]
        };
    }
    return {};
}
/**
 * Check if polyfill is forced via environment/config
 * This is useful for testing polyfill performance even on browsers with native support
 */ function isPolyfillForced() {
    // Check for global flag (can be set by test harness)
    if (typeof globalThis.__FORCE_TEMPORAL_POLYFILL__ !== "undefined") {
        return globalThis.__FORCE_TEMPORAL_POLYFILL__ === true;
    }
    // Check for URL parameter (useful for debugging)
    if (typeof window !== "undefined" && window.location) {
        const params = new URLSearchParams(window.location.search);
        if (params.get("forceTemporalPolyfill") === "true") {
            return true;
        }
    }
    // Check for localStorage setting (useful for persistent testing)
    if (typeof localStorage !== "undefined") {
        try {
            if (localStorage.getItem("forceTemporalPolyfill") === "true") {
                return true;
            }
        } catch  {
        // localStorage might be blocked
        }
    }
    return false;
}
/**
 * Creates the Temporal provider with native detection
 */ function createTemporalProvider() {
    const hasNative = hasNativeTemporalSupport();
    const forcedPolyfill = isPolyfillForced();
    const useNative = hasNative && !forcedPolyfill;
    const browserInfo = detectBrowser();
    const environmentInfo = {
        hasNativeSupport: hasNative,
        usingNative: useNative,
        forcedPolyfill,
        browser: browserInfo.name,
        browserVersion: browserInfo.version
    };
    // Select the appropriate Temporal implementation
    const TemporalImpl = useNative ? globalThis.Temporal : TemporalPolyfill;
    return {
        isNative: useNative,
        Temporal: TemporalImpl,
        environmentInfo
    };
}
/** Singleton instance of the Temporal provider */ let _provider = null;
/**
 * Gets or creates the Temporal provider singleton
 * Uses lazy initialization to avoid circular dependency issues at module load time
 */ export function getTemporalProvider() {
    if (!_provider) {
        _provider = createTemporalProvider();
    }
    return _provider;
}
/**
 * Resets the provider (useful for testing)
 */ export function resetTemporalProvider() {
    _provider = null;
}
/**
 * Force polyfill mode (useful for testing)
 * Must be called before first access to TemporalProvider
 */ export function forcePolyfillMode() {
    globalThis.__FORCE_TEMPORAL_POLYFILL__ = true;
    resetTemporalProvider();
}
/**
 * Clear forced polyfill mode
 */ export function clearForcedPolyfillMode() {
    delete globalThis.__FORCE_TEMPORAL_POLYFILL__;
    resetTemporalProvider();
}
/**
 * Gets whether we're using native Temporal (lazy evaluation)
 */ export function isUsingNativeTemporal() {
    return getTemporalProvider().isNative;
}
/**
 * Gets environment information for telemetry (lazy evaluation)
 */ export function getTemporalEnvironmentInfo() {
    return getTemporalProvider().environmentInfo;
}
/**
 * The Temporal namespace - use this instead of importing from temporal-polyfill-lite directly
 *
 * IMPORTANT: This is a Proxy that lazily initializes the provider to avoid
 * "Cannot access X before initialization" errors from circular dependencies
 * at module load time. The proxy defers actual access until the property is used.
 */ export const Temporal = new Proxy({}, {
    get (_target, prop) {
        return getTemporalProvider().Temporal[prop];
    },
    has (_target, prop) {
        return prop in getTemporalProvider().Temporal;
    },
    ownKeys (_target) {
        return Reflect.ownKeys(getTemporalProvider().Temporal);
    },
    getOwnPropertyDescriptor (_target, prop) {
        return Reflect.getOwnPropertyDescriptor(getTemporalProvider().Temporal, prop);
    }
});
